/*
    Imports
*/
import { Icon } from '@iconify/react';
import {
    Alert,
    Collapse,
    Grid,
    Stack,
    TextField,
    Typography,
    IconButton,
    InputAdornment,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Box,
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AccountDeletionSchema } from '../../config/form-schemas';
import { ShowPasswordIcon, HidePasswordIcon } from 'src/config/icons';
import userService from 'src/services/UserServiceClass';
import LoadingFormButton from 'src/components/misc/Buttons/LoadingFormButton';

/*
    Main Working
*/
export default ({ user }) => {
    /*
      States, Params, Navigation, Query.
    */
    const [serverError, setServerError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openFormDialog, setOpenFormDialog] = useState(false);
    const [submit, setSubmit] = useState(false);
    const navigate = useNavigate();

    /*
      Form Setup
    */
    const formik = useFormik({
        initialValues: {
            password: '',
            dob: null,
            ticketId: '',
        },
        validationSchema: AccountDeletionSchema,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            setServerError('');
            setSubmit(true);
            userService
                .requestAccountDeletion({
                    password: values.password,
                    dob: values.dob
                        ? `${values.dob.getFullYear()}-${
                              values.dob.getMonth() + 1
                          }-${values.dob.getDate()}`
                        : '',
                    ticketId: values.ticketId,
                })
                .then((res) => {
                    setOpenFormDialog(false);
                    setOpenDialog(true);
                    resetForm();
                })
                .catch((err) => {
                    console.error('Error', err);
                    if (!err.response) {
                        setServerError('Error occurred please try later');
                    } else {
                        setServerError(err.response.data.msg);
                    }
                })
                .finally(() => {
                    setSubmit(false);
                    setSubmitting(false);
                });
        },
    });

    const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = formik;

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const handleFormDialogOpen = () => {
        setOpenFormDialog(true);
    };

    const handleFormDialogClose = () => {
        setOpenFormDialog(false);
    };

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    return (
        <>
            <Grid container>
                <Grid item xs={12} md={12} sx={{ marginTop: 3, marginBottom: 4 }}>
                    <Typography variant="h6">Delete Account</Typography>
                </Grid>
            </Grid>

            <Grid container spacing={0} sx={{ marginTop: 2 }}>
                <Grid item xs={12} md={6}>
                    <LoadingFormButton
                        size="large"
                        type="button"
                        variant="outlined"
                        color="error"
                        onClick={handleFormDialogOpen}
                        sx={{ p: '0px 37px' }}
                    >
                        Request Account Deletion
                    </LoadingFormButton>
                </Grid>
            </Grid>

            {/* Form Dialog */}
            <Dialog
                open={openFormDialog}
                onClose={handleFormDialogClose}
                aria-labelledby="form-dialog-title"
                aria-describedby="form-dialog-description"
                maxWidth="md"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">Account Deletion Request</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
                        Please note that account deletion requests are subject to review and may
                        take up to 30 days to process. All your data will be permanently deleted
                        upon approval.
                    </Typography>

                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="password"
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        {...getFieldProps('password')}
                                        error={Boolean(touched.password && errors.password)}
                                        helperText={touched.password && errors.password}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={handleShowPassword}
                                                        edge="end"
                                                    >
                                                        <Icon
                                                            icon={
                                                                showPassword
                                                                    ? HidePasswordIcon
                                                                    : ShowPasswordIcon
                                                            }
                                                        />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="Date of Birth"
                                            value={formik.values.dob}
                                            onChange={(date) => setFieldValue('dob', date)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    error={touched.dob && Boolean(errors.dob)}
                                                    helperText={touched.dob && errors.dob}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="ticketId"
                                        label="Support Ticket ID"
                                        {...getFieldProps('ticketId')}
                                        error={Boolean(touched.ticketId && errors.ticketId)}
                                        helperText={touched.ticketId && errors.ticketId}
                                    />
                                </Grid>
                            </Grid>

                            {serverError && (
                                <Stack sx={{}} marginTop={3}>
                                    <Collapse in={!!serverError}>
                                        <Alert severity="error">{serverError}</Alert>
                                    </Collapse>
                                </Stack>
                            )}
                        </Form>
                    </FormikProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFormDialogClose}>Cancel</Button>
                    <LoadingFormButton
                        type="button"
                        variant="contained"
                        color="error"
                        loading={submit}
                        onClick={() => {
                            handleSubmit();
                        }}
                    >
                        Submit Request
                    </LoadingFormButton>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Account Deletion Request Submitted
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Your account deletion request has been submitted successfully. An
                        administrator will review your request. You will be notified once the review
                        is complete.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
